import type {
    CustomSeriesRenderItemAPI,
    CustomSeriesRenderItemParams,
    CustomSeriesRenderItemReturn,
    GridComponentOption,
    XAXisComponentOption,
    YAXisComponentOption,
} from "echarts";
import type { CustomSeriesOption } from "echarts/charts";
import { graphic } from "echarts/core";
import type { TimeEvent } from "../../band-data/types";
import type { PlotLayout } from "../layout/plot-layout";
import {
    ACTUAL_COLOR,
    BAND_BACKGROUND,
    BAR_INSET_PX,
    BAR_RADIUS_PX,
    bandRowBox,
    MIN_BAR_PX,
    SCHEDULED_BORDER,
    SCHEDULED_BORDER_PX,
    SCHEDULED_FILL,
} from "./band-geometry";
import { mostOverlapping } from "./spans-at-time";

const BAND_GRID_ID = "band";
const BAND_X_AXIS_ID = "band-time";
const BAND_Y_AXIS_ID = "band-value";

type TimeWindow = { startMs: number; endMs: number };
type BandRole = "scheduled" | "actual";
type PixelRect = { x: number; y: number; width: number; height: number };

/** Round flags are 0 where the window cut the event or it is still running; a valve bar also carries its box. */
export type BandRow = [
    startMs: number,
    endMs: number,
    roundLeft: 0 | 1,
    roundRight: 0 | 1,
    boxStartMs?: number | null,
    boxEndMs?: number | null,
];

const endOf = (event: TimeEvent, window: TimeWindow, nowMs: number) => event.endMs ?? Math.min(nowMs, window.endMs);

export function toBandRows(events: TimeEvent[], window: TimeWindow, nowMs: number, boxes?: TimeEvent[]): BandRow[] {
    return events.flatMap((event): BandRow[] => {
        const running = event.endMs === null;
        const endMs = endOf(event, window, nowMs);
        if (endMs <= window.startMs || event.startMs >= window.endMs || endMs < event.startMs) return [];

        const startMs = Math.max(event.startMs, window.startMs);
        const clippedEndMs = Math.min(endMs, window.endMs);
        const roundLeft = event.startMs >= window.startMs ? 1 : 0;
        const roundRight = !running && endMs <= window.endMs ? 1 : 0;
        if (!boxes) return [[startMs, clippedEndMs, roundLeft, roundRight]];

        const box = mostOverlapping(event, boxes, (candidate) => endOf(candidate, window, nowMs));
        return [
            [
                startMs,
                clippedEndMs,
                roundLeft,
                roundRight,
                box?.startMs ?? null,
                box ? endOf(box, window, nowMs) : null,
            ],
        ];
    });
}

type EndRadii = [top: number, bottom: number];

function endRadii(role: BandRole, cut: boolean, barX: number, boxX: number | undefined, outward: 1 | -1): EndRadii {
    if (cut) return [0, 0];
    if (role === "scheduled" || boxX === undefined) return [BAR_RADIUS_PX, BAR_RADIUS_PX];
    const pastBox = (barX - boxX) * outward;
    if (pastBox < -BAR_RADIUS_PX) return [0, 0];
    if (pastBox <= BAR_RADIUS_PX) return [0, BAR_RADIUS_PX];
    return [BAR_RADIUS_PX, BAR_RADIUS_PX];
}

export function renderBandBar(
    role: BandRole,
    params: CustomSeriesRenderItemParams,
    api: CustomSeriesRenderItemAPI,
): CustomSeriesRenderItemReturn {
    const [startX = 0, bandBottomY = 0] = api.coord([Number(api.value(0)), 0]);
    // Valve open fills the bottom half so the scheduled box shows above it, as the legacy strip drew it.
    const [endX = 0, barTopY = 0] = api.coord([Number(api.value(1)), role === "scheduled" ? 1 : 0.5]);
    const topY = role === "scheduled" ? barTopY + BAR_INSET_PX : barTopY;
    // The box's border straddles its edge, so the valve bar reaches half a border further to cover it.
    const bottomY = bandBottomY - BAR_INSET_PX + (role === "actual" ? SCHEDULED_BORDER_PX / 2 : 0);
    // ECharts gives NaN for a missing box end.
    const boxX = (dim: number) => {
        const ms = Number(api.value(dim));
        return Number.isFinite(ms) ? api.coord([ms, 0])[0] : undefined;
    };
    const left = endRadii(role, Number(api.value(2)) !== 1, startX, boxX(4), -1);
    const right = endRadii(role, Number(api.value(3)) !== 1, endX, boxX(5), 1);

    const coordSys = params.coordSys as CustomSeriesRenderItemParams["coordSys"] & PixelRect;
    const clipped = graphic.clipRectByRect(
        { x: startX, y: topY, width: Math.max(endX - startX, MIN_BAR_PX), height: bottomY - topY },
        { x: coordSys.x, y: coordSys.y, width: coordSys.width, height: coordSys.height },
    );
    if (!clipped || clipped.width <= 0) return undefined;

    return {
        type: "rect",
        silent: true,
        shape: { ...clipped, r: [left[0], right[0], right[1], left[1]] },
        style:
            role === "scheduled"
                ? { fill: SCHEDULED_FILL, stroke: SCHEDULED_BORDER, lineWidth: SCHEDULED_BORDER_PX }
                : { fill: ACTUAL_COLOR },
    };
}

export function buildBandGrid(layout: PlotLayout, position: "above" | "below"): GridComponentOption {
    return {
        id: BAND_GRID_ID,
        show: true,
        containLabel: false,
        backgroundColor: BAND_BACKGROUND,
        borderWidth: 0,
        left: layout.gutters.left,
        right: layout.gutters.right,
        ...bandRowBox(layout, position),
    };
}

export function buildBandAxes(window: TimeWindow): { xAxis: XAXisComponentOption; yAxis: YAXisComponentOption } {
    return {
        xAxis: {
            id: BAND_X_AXIS_ID,
            type: "time",
            gridIndex: 1,
            min: window.startMs,
            max: window.endMs,
            show: false,
            axisPointer: { show: true, label: { show: false } },
        },
        yAxis: { id: BAND_Y_AXIS_ID, type: "value", gridIndex: 1, min: 0, max: 1, show: false },
    };
}

export function buildBandSeries(
    scheduled: TimeEvent[],
    actual: TimeEvent[],
    window: TimeWindow,
    nowMs: number,
): CustomSeriesOption[] {
    const series = (role: BandRole, events: TimeEvent[], boxes?: TimeEvent[]): CustomSeriesOption => ({
        id: `band-${role}`,
        type: "custom",
        // By id: the band's y-axis comes after however many y-axes the plot has, so an index would drift.
        xAxisId: BAND_X_AXIS_ID,
        yAxisId: BAND_Y_AXIS_ID,
        silent: true,
        // Out of the axis tooltip so the crosshair does not snap to bar starts over the band.
        tooltip: { show: false },
        renderItem: (params, api) => renderBandBar(role, params, api),
        data: toBandRows(events, window, nowMs, boxes),
    });

    // Scheduled first so the valve-open bars paint over the boxes.
    return [series("scheduled", scheduled), series("actual", actual, scheduled)];
}
